import React, { useContext } from "react";

import { groupBy } from "@bps/common-utils";
import {
  ButtonsGroupOption,
  Heading,
  NoDataTile,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ProviderAccContractTypeDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { ButtonsGroupSingleChoiceField } from "@ui-components/form/ButtonsGroupSingleChoiceField.tsx";
import { useFieldArray } from "@ui-components/form/submission-form/hooks/useFieldArray.ts";

import { ProviderAccContractContext } from "./context/ProviderAccContractContext.tsx";
import { getProviderAccContractStylesSet } from "./ProviderAccContracts.styles.ts";
import {
  NO_ACC_CONTRACT,
  ProviderAccContractsFormLabels
} from "./ProviderAccContracts.types.ts";

const ProviderAccContractsBase: React.FC = () => {
  const { core } = useStores();
  const theme = useTheme();

  const accContractHelper = useContext(ProviderAccContractContext);

  const accProviderTypes = accContractHelper.availableAccProviderTypes;
  const providerTextTypes = accProviderTypes.map(i => i.text);

  const contractFields = useFieldArray("contractTypes").fields;
  const practiceContractTypes =
    accContractHelper.originalPracticeAccContractCompanyData;

  const providerContractTypes = accContractHelper.getProviderAccContractType(
    practiceContractTypes
  );

  const groupByProviderType = groupBy(
    providerContractTypes,
    item => item.providerTypeText
  );

  const findFieldsIndex = (key: string) =>
    contractFields.value.findIndex(v => v.providerTypeText === key);

  const getButtonOptions = (
    providerTypeText: string,
    disabled: boolean
  ): ButtonsGroupOption<string>[] => {
    const index = findFieldsIndex(providerTypeText);

    const noAccContractOptionButton = {
      key: `${providerTypeText}${NO_ACC_CONTRACT}`,
      text: "No ACC Contract",
      disabled
    };

    const types = Object.fromEntries(groupByProviderType)[providerTypeText];
    if (index >= 0 && types !== undefined) {
      return [
        ...types.map((g: ProviderAccContractTypeDto) => ({
          key: g.code,
          text: g.text,
          disabled
        })),
        noAccContractOptionButton
      ];
    }

    return [{ ...noAccContractOptionButton, disabled: true }];
  };

  const providerAccContractStyles = getProviderAccContractStylesSet(theme);

  const hasUserWritePermission = core.hasPermissions(Permission.UserWrite);

  return (
    <>
      {providerTextTypes.map(typeText => {
        const index = findFieldsIndex(typeText);

        const subtext = contractFields.value[index]?.subtext;

        const noAccContract: boolean =
          contractFields.value[index]?.providerContractTypeCode.includes(
            NO_ACC_CONTRACT
          );

        return (
          <Stack
            key={typeText}
            tokens={{ childrenGap: 0 }}
            styles={providerAccContractStyles.lineStackRoot}
          >
            <Heading
              variant="section-heading-light"
              styles={providerAccContractStyles.providerTypeLineStackItem}
            >
              {typeText}
            </Heading>
            <Stack
              horizontal
              tokens={{ childrenGap: 10 }}
              styles={providerAccContractStyles.lineStackItem}
            >
              <ButtonsGroupSingleChoiceField
                disabled={!hasUserWritePermission}
                options={getButtonOptions(typeText, !hasUserWritePermission)}
                required
                name={`contractTypes[${index}].providerContractTypeCode`}
                label="Contract"
              />
            </Stack>
            {!noAccContract && (
              <Text styles={providerAccContractStyles.codeText}>{subtext}</Text>
            )}
          </Stack>
        );
      })}

      {!contractFields.value.length && (
        <NoDataTile
          textProps={{ text: ProviderAccContractsFormLabels.noProviderType }}
          linkProps={{ hidden: true }}
        />
      )}
    </>
  );
};

export const ProviderAccContracts = withFetch(
  x => [x.practice.ref.accProviderContractTypes.load()],
  ProviderAccContractsBase
);
